// users.js
// User-related helper functions

/**
 * Generate a consistent color for a user based on their UID
 * Uses a hash of the UID to pick from a palette of distinct colors
 */
export function generateUserColor(uid) {
    const colors = [
        "#2e7dff", // blue
        "#ff6b6b", // red
        "#51cf66", // green
        "#ffd43b", // yellow
        "#a78bfa", // purple
        "#ff8787", // coral
        "#22b8cf", // cyan
        "#ff922b", // orange
        "#f06595", // pink
        "#94d82d", // lime
        "#ff6b9d", // rose
        "#4dabf7", // light blue
        "#845ef7", // violet
        "#20c997", // teal
        "#ff8c42", // tangerine
    ];

    // Simple hash function to convert UID to a number
    let hash = 0;
    for (let i = 0; i < uid.length; i++) {
        hash = ((hash << 5) - hash) + uid.charCodeAt(i);
        hash = hash & hash; // Convert to 32bit integer
    }

    // Use absolute value and modulo to get a consistent index
    const index = Math.abs(hash) % colors.length;
    return colors[index];
}

/**
 * Resolve a human-readable label for collaboration cursors.
 * y-prosemirror reads {@code user.name} (not displayName).
 */
export function resolveAwarenessUserName(user) {
    if (!user) {
        return 'Anonymous';
    }
    const fromParts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return (
        user.displayName ||
        fromParts ||
        user.email ||
        user.uid ||
        'Anonymous'
    );
}

/**
 * Convert a user object to awareness user format
 * @param {Object} user - User object with uid, email, displayName, etc.
 * @param {{clientSessionId?: string, clientSessionStart?: string}} [trace] - Browser session for LLM audit
 * @returns {Object} Awareness user object
 */
export function toAwarenessUser(user, trace = {}) {
    const name = resolveAwarenessUserName(user);
    return {
        uid: user.uid,
        id: user.uid,
        name,
        email: user.email || null,
        displayName: user.displayName || name,
        photoURL: user.photoURL || null,
        color: user.color || generateUserColor(user.uid),
        ...(trace.clientSessionId ? {clientSessionId: trace.clientSessionId} : {}),
        ...(trace.clientSessionStart ? {clientSessionStart: trace.clientSessionStart} : {}),
    };
}

/**
 * Build a remote caret element for another user
 * Uses pseudo-elements to avoid disrupting text flow and line height
 * Leverages CSS from editor.css (.pm-remote-caret)
 * @param {Object} awarenessUser - Awareness user object
 * @returns {HTMLElement} Caret element
 */
export function buildRemoteCaretElement(awarenessUser) {
    const caret = document.createElement("span");
    caret.className = "pm-remote-caret";

    // Set custom property for the color (used by ::before and ::after pseudo-elements)
    caret.style.setProperty("--pm-caret-color", awarenessUser.color);

    const label =
        awarenessUser.name ||
        awarenessUser.displayName ||
        awarenessUser.email ||
        awarenessUser.uid ||
        'Unknown';
    caret.setAttribute("data-name", label);

    return caret;
}
